/**
 * REV2 R3/C3 (bulk-onboarding decision note): prefixes a leading single quote onto any value that
 * would otherwise be interpreted by Excel/Sheets/LibreOffice as a formula — the same
 * {@code RISKY_PREFIXES} set the backend's XLSX-only {@code ExportSanitizer} uses
 * ({@code = + - @ \t \r}). {@link downloadCsv} now applies it to every string cell itself; call it directly
 * only for CSV text built some other way.
 */
export function sanitizeForSpreadsheet(value: string | null | undefined): string {
    if (value === null || value === undefined) return '';
    const s = String(value);
    return /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
}

/** A formatted signed number or phone ("-1,234.50", "+880 1700-000001") cannot be a formula, so it keeps its sign. */
const PLAIN_SIGNED_NUMBER = /^[+-][0-9 ().,-]+$/;

/**
 * The CSV text {@link downloadCsv} saves. OWASP A03: every string cell goes through the formula guard here, so a
 * caller can't forget it (a customer named "=HYPERLINK(...)" stays text). Numbers and plain signed numbers are untouched;
 * a value already prefixed by {@link sanitizeForSpreadsheet} starts with a quote and passes through unchanged.
 */
export function toCsv(headers: string[], rows: (string | number)[][]): string {
  const safe = (v: string | number) =>
      typeof v === 'number' || PLAIN_SIGNED_NUMBER.test(v) ? String(v) : sanitizeForSpreadsheet(v);
  const esc = (v: string | number) => `"${safe(v).replace(/"/g, '""')}"`;
  // L-2: RFC 4180 specifies CRLF line endings; Excel/Sheets tolerate a bare \n, but this is the
  // one place this module deviated from the RFC-4180 quoting it otherwise implements correctly.
  return [headers, ...rows].map(r => r.map(esc).join(',')).join('\r\n');
}

export function downloadCsv(filename: string, headers: string[], rows: (string | number)[][]): void {
  const body = toCsv(headers, rows);
  const blob = new Blob(['﻿' + body], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
